import React, { useEffect, useMemo } from "react";
import {
  AccessEnum,
  UserMaterializedPermissionsDto,
  useUserApi,
} from "~/api/GeneratedApi";
import {
  selectAccesses,
  selectAccessesSet,
  selectAnyAccess,
  selectTransformedAccesses,
  selectUser,
  setAccesses,
  setAccessesSet,
  setAnyAccess,
  setIsGlobalAdmin,
  setTransformedAccesses,
} from "~/redux/reducers/authReducer";
import { useAppDispatch, useAppSelector } from "~/redux/store/store";
import { InSeaHubProvider } from "~/signalr/InSeaHubProvider";
import LoadingIndicator from "~/template/components/LoadingIndicator";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";

type Props = {
  children: JSX.Element;
};
export type UserAccess = Record<AccessKey, boolean>;

export type AccessKeyRecord = Record<AccessEnum, keyof typeof AccessEnum>;

type AccessKey = keyof typeof AccessEnum;

export type Accesses = Record<AccessEnum, boolean>;

type GetAccessArg = number | number[] | undefined;
export function apiAccessesToState(
  apiAccess: UserMaterializedPermissionsDto,
  mapAreaId?: GetAccessArg,
): UserAccess {
  const accessess: Record<string, boolean> = {};
  const isGlobalAdmin = apiAccess.globalAdmin;
  Object.keys(AccessEnum).forEach((a) => {
    const key = a as keyof typeof AccessEnum;
    // Find the related permission
    const perm = apiAccess.permissions?.find(
      (p) =>
        p.accessId === AccessEnum[key] &&
        (!mapAreaId ||
          !p.mapAreaId ||
          (Array.isArray(mapAreaId)
            ? mapAreaId.includes(p.mapAreaId)
            : mapAreaId === p.mapAreaId)),
    );
    accessess[key] = isGlobalAdmin || !!perm;
    // accessess[key] = key !== "FerryUpdate"; // Testing things can be put here
  });
  return accessess as UserAccess;
}

export const AccessWrapper = ({ children }: Props): JSX.Element => {
  const dispatch = useAppDispatch();
  const { getCurrentUserAccesses } = useUserApi();

  const accesses = useAppSelector(selectAccesses);
  const accessesSet = useAppSelector(selectAccessesSet);
  const storeTA = useAppSelector(selectTransformedAccesses);
  const anyAccess = useAppSelector(selectAnyAccess);

  const storeUser = useAppSelector(selectUser);

  React.useEffect(() => {
    if (!accesses || !storeTA || !anyAccess || !storeUser) return;

    dispatch(setAccessesSet(true));
  }, [accesses, accessesSet, dispatch, anyAccess, storeTA, storeUser]);

  useEffect(() => {
    if (!accesses || !storeUser) return;
    dispatch(setAnyAccess(apiAccessesToState(accesses)));

    dispatch(setIsGlobalAdmin(accesses.globalAdmin));
  }, [accesses, dispatch, storeUser]);

  const transformedAccesses: Accesses | null = useMemo(
    () =>
      anyAccess
        ? Object.keys(AccessEnum).reduce(
            (prev, cur) => ({
              ...prev,
              [AccessEnum[cur as keyof typeof AccessEnum]]:
                anyAccess[cur as keyof typeof AccessEnum],
            }),
            {},
          )
        : null,
    [anyAccess],
  ) as Accesses;

  React.useEffect(() => {
    if (!storeUser) return;
    dispatch(setTransformedAccesses(transformedAccesses));
  }, [transformedAccesses, dispatch, storeUser]);

  useCustomQuery("current-user-accesses", () => getCurrentUserAccesses(), {
    onSuccess: (d) => {
      dispatch(setAccesses(d));
    },
    refetchOnWindowFocus: false,
    retry: false,
    enabled: storeUser !== null,
  });

  if (!accessesSet) return <LoadingIndicator />;
  return <InSeaHubProvider>{children}</InSeaHubProvider>;
};
